/**
 * Enum API client
 *
 * All requests go to /api/enums via the existing axiosInstance
 * (base URL = REACT_APP_DYNAMO_API_URL).
 */

import axiosInstance from '../utils/axio-instance';

const BASE = '/enums';

// ─── Public reads ─────────────────────────────────────────────────────────────

/**
 * Get flat list of active options for a namespace.
 * @param {string} namespace  e.g. 'user.roles'
 * @returns {Promise<Array<{value, label, color, icon, flags, parentValue, path, depth, order}>>}
 */
export async function getEnumOptions(namespace) {
  const res = await axiosInstance.get(`${BASE}/${namespace}/options`);
  return res.data?.data ?? [];
}

/**
 * Get the full value tree (hierarchical namespaces like property.types).
 * @param {string} namespace
 * @returns {Promise<Array<EnumTreeNode>>}
 */
export async function getEnumTree(namespace) {
  const res = await axiosInstance.get(`${BASE}/${namespace}/tree`);
  return res.data?.data ?? [];
}

/**
 * Get direct children of a parent value.
 * @param {string} namespace
 * @param {string} parentValue
 */
export async function getEnumChildren(namespace, parentValue) {
  const res = await axiosInstance.get(`${BASE}/${namespace}/children/${parentValue}`);
  return res.data?.data ?? [];
}

/**
 * Get all descendants of a parent value (full subtree, flat list).
 * @param {string} namespace
 * @param {string} parentValue
 */
export async function getEnumDescendants(namespace, parentValue) {
  const res = await axiosInstance.get(`${BASE}/${namespace}/descendants/${parentValue}`);
  return res.data?.data ?? [];
}

/**
 * Get ancestor chain of a value (root → value, flat list).
 * @param {string} namespace
 * @param {string} value
 */
export async function getEnumAncestors(namespace, value) {
  const res = await axiosInstance.get(`${BASE}/${namespace}/ancestors/${value}`);
  return res.data?.data ?? [];
}

/**
 * List all namespace summaries (admin catalogue).
 * @param {{ domain?, category?, search? }} filters
 */
export async function listEnumNamespaces(filters = {}) {
  const res = await axiosInstance.get(BASE, { params: filters });
  return res.data?.data ?? [];
}

// ─── Admin reads ──────────────────────────────────────────────────────────────

/**
 * Get full namespace document (admin only).
 * @param {string} namespace
 */
export async function getNamespaceAdmin(namespace) {
  const res = await axiosInstance.get(`${BASE}/admin/${namespace}`);
  return res.data?.data ?? null;
}

/**
 * Get audit log for a namespace (admin only).
 * @param {string} namespace
 * @param {number} [limit=50]
 */
export async function getEnumAuditLog(namespace, limit = 50) {
  const res = await axiosInstance.get(`${BASE}/admin/${namespace}/audit`, {
    params: { limit },
  });
  return res.data?.data ?? [];
}

// ─── Admin writes ─────────────────────────────────────────────────────────────

/**
 * Create a new namespace (admin only).
 * @param {string} namespace
 * @param {{ label, domain, category, hierarchical?, values? }} payload
 */
export async function createNamespace(namespace, payload) {
  const res = await axiosInstance.post(`${BASE}/admin/${namespace}`, payload);
  return res.data;
}

/**
 * Soft-delete a namespace (admin only).
 * @param {string} namespace
 */
export async function deleteNamespace(namespace) {
  const res = await axiosInstance.delete(`${BASE}/admin/${namespace}`);
  return res.data;
}

/**
 * Add a value to an existing namespace (admin only).
 * @param {string} namespace
 * @param {{ value, label, parentValue?, path?, depth?, order?, color?, icon?, flags? }} payload
 */
export async function addEnumValue(namespace, payload) {
  const res = await axiosInstance.post(`${BASE}/admin/${namespace}/values`, payload);
  return res.data;
}

/**
 * Update a value in a namespace (admin only).
 * @param {string} namespace
 * @param {string} valueKey
 * @param {Partial<EnumValue>} patch
 */
export async function updateEnumValue(namespace, valueKey, patch) {
  const res = await axiosInstance.put(`${BASE}/admin/${namespace}/values/${valueKey}`, patch);
  return res.data;
}

/**
 * Deprecate (soft-delete) a value (admin only).
 * @param {string} namespace
 * @param {string} valueKey
 * @param {{ reason?, replacedBy? }} payload
 */
export async function deprecateEnumValue(namespace, valueKey, payload = {}) {
  const res = await axiosInstance.post(
    `${BASE}/admin/${namespace}/values/${valueKey}/deprecate`,
    payload,
  );
  return res.data;
}

/**
 * Restore a deprecated value (admin only).
 * @param {string} namespace
 * @param {string} valueKey
 */
export async function restoreEnumValue(namespace, valueKey) {
  const res = await axiosInstance.post(
    `${BASE}/admin/${namespace}/values/${valueKey}/restore`,
  );
  return res.data;
}

/**
 * Reorder values in a namespace (admin only).
 * @param {string} namespace
 * @param {string[]} orderedValues  Array of value keys in desired order
 */
export async function reorderEnumValues(namespace, orderedValues) {
  const res = await axiosInstance.put(`${BASE}/admin/${namespace}/reorder`, { orderedValues });
  return res.data;
}
